
import { SearchResult, Coordinates } from "../types";
import { detectCountry } from "../utils/countryDetection";
import i18n from "../i18n";

// Fallback for Photon-resultater som mangler landskode
const detectCountryFromCoords = (lat: number, lon: number): string | null => detectCountry(lat, lon);

/**
 * Fold nordiske diakritiske tegn til basisbokstav for søke-matching, så norsk/svensk/
 * dansk stavemåte treffer hverandre («Gøteborg»↔«Göteborg», «Malmø»↔«Malmö»). Ren.
 */
export const foldNordic = (s: string): string =>
  (s || '').toLowerCase()
    .replace(/[øö]/g, 'o').replace(/[æä]/g, 'a').replace(/å/g, 'a')
    .replace(/ü/g, 'u').replace(/é/g, 'e');

/**
 * Søker etter steder — kun i aktiverte land.
 * Bruker Nominatim (med countrycodes) for presise treff og Photon for utfyllende treff.
 * allowedCountries styrer hvilke land som returneres (f.eks. ['no'] eller ['no','se']).
 */
export const searchPlace = async (query: string, allowedCountries: string[] = ['no']): Promise<SearchResult[]> => {
  if (!query || query.length < 3) return [];

  const allowedUpper = new Set(allowedCountries.map(c => c.toUpperCase()));

  try {
    const queryEncoded = encodeURIComponent(query);
    const lang = (i18n.resolvedLanguage || i18n.language || "no").toLowerCase().startsWith("en") ? "en" : "default";
    const nominatimLang = lang === "en" ? "en" : "no";
    const countryCodes = allowedCountries.join(',');

    // To parallelle søk: Nominatim for aktiverte land, Photon for utfyllende treff
    const [nordicResponse, globalResponse] = await Promise.all([
      fetch(
        `https://nominatim.openstreetmap.org/search?q=${queryEncoded}&format=json&limit=8&countrycodes=${countryCodes}&accept-language=${nominatimLang}`,
        { headers: { "User-Agent": "Fricamp/1.0 (https://fricamp.com)" } }
      ),
      fetch(`https://photon.komoot.io/api/?q=${queryEncoded}&limit=15&lat=60.47&lon=8.46&lang=${lang}`)
    ]);

    // Parse Nominatim-resultater (aktiverte land)
    const nordicResults: any[] = [];
    if (nordicResponse.ok) {
      const nordicData = await nordicResponse.json();
      for (const r of nordicData) {
        nordicResults.push({
          display_name: r.display_name,
          lat: r.lat,
          lon: r.lon,
          type: r.type || "place",
          countrycode: (r.display_name?.includes("Norge") || r.display_name?.includes("Norway")) ? "NO" : "SE",
          boundingbox: r.boundingbox || [
            (parseFloat(r.lat) - 0.01).toString(), (parseFloat(r.lat) + 0.01).toString(),
            (parseFloat(r.lon) - 0.01).toString(), (parseFloat(r.lon) + 0.01).toString()
          ]
        });
      }
    }

    // Parse Photon-resultater — filtrer til kun aktiverte land
    const photonResults: any[] = [];
    if (globalResponse.ok) {
      const globalData = await globalResponse.json();
      for (const f of (globalData.features || [])) {
        const p = f.properties;
        const cc = p.countrycode?.toUpperCase();
        // Filtrer bort resultater fra land som ikke er aktivert
        if (cc && !allowedUpper.has(cc)) continue;
        // Resultater uten landskode: sjekk om koordinatene er innenfor aktiverte land
        if (!cc) {
          const lon = f.geometry.coordinates[0];
          const lat = f.geometry.coordinates[1];
          const detected = detectCountryFromCoords(lat, lon);
          if (!detected || !allowedUpper.has(detected)) continue;
        }

        const coords = f.geometry.coordinates;
        const parts = [
          p.name,
          p.city || p.town || p.district,
          p.state || p.county,
          p.country
        ].filter(Boolean);

        photonResults.push({
          display_name: parts.join(", "),
          lat: coords[1].toString(),
          lon: coords[0].toString(),
          type: p.osm_value || p.osm_key || p.type || "place",
          countrycode: cc,
          boundingbox: p.extent ? [
            p.extent[1].toString(), p.extent[3].toString(),
            p.extent[0].toString(), p.extent[2].toString()
          ] : [
            (coords[1] - 0.01).toString(), (coords[1] + 0.01).toString(),
            (coords[0] - 0.01).toString(), (coords[0] + 0.01).toString()
          ]
        });
      }
    }

    // Kombiner og fjern duplikater basert på koordinater
    const seen = new Set<string>();
    const results: any[] = [];
    const q = query.trim().toLowerCase();
    const qf = foldNordic(query.trim()); // nordisk-foldet for stavevariant-match

    for (const r of [...nordicResults, ...photonResults]) {
      const latLonKey = `${parseFloat(r.lat).toFixed(3)},${parseFloat(r.lon).toFixed(3)}`;
      if (seen.has(latLonKey)) continue;
      seen.add(latLonKey);
      results.push(r);
    }

    const placeTypePriority = new Set([
      "city", "town", "village", "hamlet", "locality",
      "administrative", "county", "state", "region", "place",
      "suburb", "quarter", "neighborhood", "municipality"
    ]);
    // Destinasjons-POI-er folk faktisk søker på ved navn (fornøyelsesparker, attraksjoner, museer …)
    const destinationPoi = new Set([
      "theme_park", "zoo", "water_park", "aquarium", "attraction", "museum",
      "viewpoint", "castle", "monument", "artwork", "historic", "ruins", "waterfall"
    ]);
    // Støy-POI-er som sjelden er søkemålet (holdeplasser, ladere, hjertestartere, skilt …)
    const noisePoi = new Set([
      "bus_stop", "defibrillator", "guidepost", "charging_station", "ticket",
      "tower", "vending_machine", "bench", "information", "waste_basket", "street_lamp"
    ]);

    // Sorteringslogikk:
    // 1) Treffkvalitet på søkestreng (eksakt > prefiks > delstreng) — relevans først
    // 2) Type-klasse: stedsnavn → destinasjons-POI → nøytral → støy-POI
    //    (slik at f.eks. Tusenfryd-fornøyelsesparken ikke begraves under bussholdeplasser)
    // 3) Kortere navn først
    console.log(`SearchPlace [${query}] found ${results.length} results (${nordicResults.length} nominatim, ${photonResults.length} photon), countries: ${countryCodes}`);
    return results.sort((a, b) => {
      const getClassRank = (res: any) => {
        const t = String(res.type || "").toLowerCase();
        if (placeTypePriority.has(t)) return 0;
        if (destinationPoi.has(t)) return 1;
        if (noisePoi.has(t)) return 3;
        return 2;
      };
      const getTextRank = (res: any) => {
        const name = String(res.display_name || "").toLowerCase();
        if (name === q) return 0;
        if (name.startsWith(q)) return 1;
        if (name.includes(q)) return 2;
        // Nordisk-foldet match (Gøteborg↔Göteborg) — litt lavere enn eksakt tekst.
        const nf = foldNordic(name);
        if (nf === qf) return 0;
        if (nf.startsWith(qf)) return 1;
        if (nf.includes(qf)) return 2;
        return 3;
      };

      const textA = getTextRank(a);
      const textB = getTextRank(b);
      if (textA !== textB) return textA - textB;
      const classA = getClassRank(a);
      const classB = getClassRank(b);
      if (classA !== classB) return classA - classB;
      return String(a.display_name || "").length - String(b.display_name || "").length;
    }).slice(0, 10);

  } catch (error) {
    console.error("Geocoding error for query [" + query + "]:", error);
    return [];
  }
};

/** Sjekk om en streng bare er en koordinat-label (f.eks. "60.3287°N, 10.4321°E"). */
export const isCoordinateLabel = (s: string): boolean => /^\d+\.\d+°[NSEW]/.test(s);

/** Trekk ut stedsnavn fra Photon/Nominatim properties. Returnerer '' hvis ingenting nyttig finnes. */
const extractPlaceName = (p: Record<string, any>): string => {
  const name = p.name;
  const city = p.city || p.town || p.district || p.village || p.locality;
  const county = p.county;
  const street = p.street;

  if (name && city && name !== city) return `${name}, ${city}`;
  if (name) return name;
  if (street && city) return `${street}, ${city}`;
  if (city && county) return `${city}, ${county}`;
  if (city) return city;
  if (county) return county;
  return p.state || p.country || '';
};

/**
 * Finn adresse eller navn basert på koordinater (Reverse Geocoding).
 * Prøver Photon først, deretter Nominatim som fallback.
 */
// Reverse-geocode til ADMINISTRATIVT område (kommune/by), IKKE veinavn. Brukes av
// reise-copiloten for lesbare stopp-navn — spesielt i Sverige der geonorge (norsk)
// ikke gir kommune. Cachet per grovt koordinat.
const reverseAreaCache = new Map<string, string>();
export const reverseArea = async (coords: Coordinates): Promise<string> => {
  const key = `${coords.latitude.toFixed(3)},${coords.longitude.toFixed(3)}`;
  const cached = reverseAreaCache.get(key);
  if (cached !== undefined) return cached;
  // Photon støtter kun en/de/fr/default — «no» gir 400. Bruk 'default' for norsk.
  const lang = (i18n.resolvedLanguage || i18n.language || 'no').toLowerCase().startsWith('en') ? 'en' : 'default';
  let area = '';
  try {
    const r = await fetch(
      `https://photon.komoot.io/reverse/?lon=${coords.longitude}&lat=${coords.latitude}&lang=${lang}`,
      { signal: AbortSignal.timeout(6000) },
    );
    if (r.ok) {
      const p = (await r.json())?.features?.[0]?.properties || {};
      area = p.city || p.town || p.village || p.district || p.county || p.state || '';
    }
  } catch { /* best-effort */ }
  reverseAreaCache.set(key, area);
  return area;
};

export const reverseGeocode = async (coords: Coordinates): Promise<string> => {
  const coordLabel = `${Math.abs(coords.latitude).toFixed(4)}°${coords.latitude >= 0 ? 'N' : 'S'}, ${Math.abs(coords.longitude).toFixed(4)}°${coords.longitude >= 0 ? 'E' : 'W'}`;
  // Photon: 'no' gir 400 → 'default'; Nominatim (fallback under) tåler 'no'.
  const photonLang = (i18n.resolvedLanguage || i18n.language || "no").toLowerCase().startsWith("en") ? "en" : "default";
  const lang = (i18n.resolvedLanguage || i18n.language || "no").toLowerCase().startsWith("en") ? "en" : "no";

  // 1. Prøv Photon
  try {
    const response = await fetch(
      `https://photon.komoot.io/reverse/?lon=${coords.longitude}&lat=${coords.latitude}&lang=${photonLang}`,
      { signal: AbortSignal.timeout(8000) }
    );
    if (response.ok) {
      const data = await response.json();
      const feature = data.features?.[0];
      if (feature) {
        const result = extractPlaceName(feature.properties);
        if (result) return result;
      }
    }
  } catch (error) {
    console.warn("Photon reverse geocoding failed:", error);
  }

  // 2. Fallback til Nominatim
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json&zoom=14&accept-language=${lang}`,
      { headers: { "User-Agent": "Fricamp/1.0 (https://fricamp.com)" }, signal: AbortSignal.timeout(8000) }
    );
    if (response.ok) {
      const data = await response.json();
      if (data && !data.error) {
        const addr = data.address || {};
        const result = extractPlaceName({
          name: data.name && data.name !== data.display_name ? data.name : undefined,
          city: addr.city || addr.town || addr.village || addr.hamlet,
          county: addr.county,
          street: addr.road,
          state: addr.state,
          country: addr.country,
        });
        if (result) return result;
      }
    }
  } catch (error) {
    console.warn("Nominatim reverse geocoding failed:", error);
  }

  return coordLabel;
};
